import * as React from "react";
import { RequestParams } from "@stemscopes/api";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import {
  useAuth,
  useTranslation,
  useQueryParams,
  useSelector,
  useResizeObserver,
  useSize,
} from "hooks";
import {
  Menu,
  MenuItem,
  SubMenu,
  NavLink,
  Link,
  Icon,
  Typography,
  VisuallyHidden,
  OfflineTools,
} from "components";
import { routes as appRoutes } from "configs";
import { MOBILE_BREAKPOINT } from "constants/breakpoints";
import { Student } from "types";
import logo from "assets/images/logo-new.svg";
import mobileLogo from "assets/images/mobile-logo.svg";
import { MenuItem as IMenuItem, HeaderProps, RoutesState } from "./duck/types";
import * as actions from "./duck/actions";
import { UserNav } from "./components";
import { headerSelectors, headerConstants, headerUtils, reducer } from "./duck";
import styles from "./Header.module.css";

const Header: React.FC<HeaderProps> = ({
  className,
  isStudentWithAssignmentHomePage = false,
  onClickNav = () => {},
}) => {
  const { t } = useTranslation();
  const [auth] = useAuth();
  const [params] = useQueryParams();
  const location = useLocation();
  const online = useSelector((state) => state.offlineMode.isOnline);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const header = React.useRef<HTMLDivElement>(null);
  const outerNav = React.useRef<HTMLDivElement>(null);
  const nav = React.useRef<HTMLUListElement>(null);
  const outerNavWidth = useResizeObserver(outerNav);
  const moreRef = React.useRef<HTMLLIElement>(null);
  const [, { height: viewportHeight, width: viewportWidth }] = useSize();
  const isHigherGradeLevel = Boolean(
    (auth.data?.profile as Student)?.gradeLevels?.match(
      headerConstants.HIGHER_GRADE_LEVELS_REG_EXP,
    ),
  );

  const isStudentFromGoogleClass =
    auth.data.user.profileType === "Student" &&
    params.google_class_room &&
    auth.data.school.googleClassroomSettings;

  const { refs, routes } = React.useMemo(
    () =>
      headerSelectors.getRoutes({
        authData: auth.data,
        t,
        online,
        isStudentWithAssignmentHomePage,
        isStudentFromGoogleClass,
        isHigherGradeLevel,
      }),
    [auth.data, online, isStudentWithAssignmentHomePage],
  );

  const logoImg = React.useMemo(() => {
    const isMobile = viewportWidth < MOBILE_BREAKPOINT;

    return isMobile ? (
      <img
        key="mobileLogo"
        src={mobileLogo}
        className={styles.mobileLogoImg}
        alt=""
      />
    ) : (
      <img key="logo" src={logo} className={styles.logoImg} alt="" />
    );
  }, [viewportWidth]);

  const buildRoutesState = React.useCallback(
    (links: IMenuItem[]): RoutesState => {
      const shownRoutes = links.filter(
        (link) => link.path && (!link.getIsVisible || link.getIsVisible(auth)),
      );

      return {
        shownRoutes,
        hiddenRoutes: [],
        hiddenItemsWidth: [],
      };
    },
    [auth],
  );

  const [routesState, dispatch] = React.useReducer(
    reducer,
    routes,
    buildRoutesState,
  );
  const { shownRoutes, hiddenRoutes, hiddenItemsWidth } = routesState;

  React.useEffect(() => {
    const newRoutesState = buildRoutesState(routes);
    dispatch(actions.switchToOffline({ newRoutesState }));
  }, [routes]);

  React.useEffect(() => {
    setHeaderHeight(header.current?.clientHeight || 0);
  }, [header.current?.clientHeight]);

  const hideRoutes = (): void => {
    const lastShownRoute = shownRoutes[shownRoutes.length - 1];
    let lastShownRouteWidth = Math.ceil(
      headerUtils.getElementWidthByRef(refs[lastShownRoute.path]),
    );
    if (lastShownRouteWidth > 0) {
      lastShownRouteWidth += headerConstants.MENU_ITEM_MARGIN;
    }

    if (!(shownRoutes.length === 1 && hiddenRoutes.length === 0)) {
      dispatch(actions.hideRoutes({ lastShownRouteWidth }));
    }
  };

  const showRoutes = (
    outerWidth: number,
    totalWidth: number,
    moreButtonOffset: number,
  ): void => {
    const freeSpace = outerWidth - totalWidth;
    let restSpace = freeSpace;
    let newShownItemsCount = 0;
    const stack = [...hiddenItemsWidth];

    while (restSpace > 0 && stack.length > 0) {
      const isLastItem = stack.length === 1;
      const nextHiddenItemsWidth = stack.pop() || 0;
      if (isLastItem) {
        restSpace += moreButtonOffset;
      }

      if (restSpace > nextHiddenItemsWidth) {
        newShownItemsCount += 1;
      }

      if (
        stack.length === 1 &&
        restSpace + moreButtonOffset > nextHiddenItemsWidth + stack[0]
      ) {
        newShownItemsCount += 2;
        break;
      }

      restSpace -= nextHiddenItemsWidth;
    }
    if (newShownItemsCount > 0) {
      dispatch(actions.showRoutes({ count: newShownItemsCount }));
    }
  };

  const handleResize = (): void => {
    if (nav.current && outerNav.current) {
      const outerWidth = Math.round(headerUtils.getElementWidthByRef(outerNav));
      const totalWidth = Math.round(headerUtils.getElementWidthByRef(nav));
      const moreButtonOffset = moreRef.current
        ? Math.ceil(headerUtils.getElementWidthByRef(moreRef)) +
          headerConstants.MENU_ITEM_MARGIN
        : 0;
      if (shownRoutes.length > 0 && outerWidth <= totalWidth) {
        hideRoutes();
      } else if (hiddenRoutes.length > 0 && outerWidth > totalWidth) {
        showRoutes(outerWidth, totalWidth, moreButtonOffset);
      }
    }
  };

  React.useEffect(() => {
    handleResize();
  }, [outerNavWidth, shownRoutes, hiddenRoutes, hiddenItemsWidth]);

  return (
    <header
      ref={header}
      id="mainHeader"
      className={classNames(className, styles.wrapper)}
    >
      <div className={styles.content}>
        <Link
          to={
            isStudentFromGoogleClass && !isHigherGradeLevel
              ? `${appRoutes.home}?google_class_room=true`
              : appRoutes.home
          }
          className={styles.logo}
        >
          {logoImg}
          <VisuallyHidden>{t("header.openHome")}</VisuallyHidden>
        </Link>
        <a href="#main" className={styles.skipLink}>
          <Typography component="span" variant="p2">
            {t("header.skipToContent")}
          </Typography>
        </a>
        <nav
          ref={outerNav}
          className={styles.navigation}
          aria-label={t("header.mainNavigation")}
        >
          <Menu
            innerRef={nav}
            popup
            aria-label={t("header.mainMenu")}
            size="small"
            hoverable={false}
            className={styles.menu}
          >
            {shownRoutes.map((link) => (
              <MenuItem
                key={link.path}
                className={styles.navItem}
                innerRef={refs[link.path]}
              >
                {link.Component ? (
                  <link.Component
                    linkClassName={styles.navLink}
                    textClassName={styles.navText}
                  />
                ) : (
                  <NavLink
                    to={link.path}
                    end={link.exact}
                    title={link.title}
                    className={({ isActive }) => {
                      const tmpIsActive =
                        (isActive ||
                          headerSelectors.isPathMatch(
                            link,
                            location.pathname,
                          )) &&
                        headerSelectors.isBundles(
                          link,
                          params as unknown as RequestParams<{
                            view: "bundles" | "recent" | "bookmarks" | "notes";
                          }>,
                        );

                      return classNames(styles.navLink, {
                        active: tmpIsActive,
                      });
                    }}
                    onClick={(event) => {
                      onClickNav(
                        event as React.MouseEvent<
                          HTMLAnchorElement,
                          MouseEvent
                        >,
                        link.path,
                      );
                      if (headerUtils.linksClickHandlers[link.text]) {
                        headerUtils.linksClickHandlers[link.text](
                          event,
                          link.path,
                          params,
                        );
                      }
                    }}
                  >
                    <Typography
                      component="span"
                      variant="p2"
                      className={styles.navText}
                    >
                      {link.text}
                    </Typography>
                  </NavLink>
                )}
              </MenuItem>
            ))}
            {hiddenRoutes.length > 0 &&
              ((
                <SubMenu
                  label={
                    <Typography variant="p2" component="span">
                      {t("header.more")}
                    </Typography>
                  }
                  popup
                  height={viewportHeight - headerHeight * 2}
                  innerRef={moreRef}
                  className={styles.navItem}
                  buttonClassName={(opened) =>
                    classNames(styles.navLink, styles.subMenuButton, {
                      [styles.opened]: opened,
                    })
                  }
                  suffix={(opened) => (
                    <Icon
                      name={opened ? "up-outlined" : "down-outlined"}
                      size="small"
                    />
                  )}
                >
                  {hiddenRoutes.map((link) => (
                    <MenuItem
                      key={link.path}
                      className={styles.subMenuItem}
                      innerRef={refs[link.path]}
                    >
                      {link.Component ? (
                        <link.Component linkClassName={styles.subMenuLink} />
                      ) : (
                        <NavLink
                          to={link.path}
                          end={link.exact}
                          title={link.title}
                          className={styles.subMenuLink}
                          onClick={(event) => {
                            onClickNav(
                              event as React.MouseEvent<
                                HTMLAnchorElement,
                                MouseEvent
                              >,
                              link.path,
                            );
                            if (headerUtils.linksClickHandlers[link.text]) {
                              headerUtils.linksClickHandlers[link.text](
                                event,
                                link.path,
                                params,
                              );
                            }
                          }}
                        >
                          <Typography variant="p2" component="span">
                            {link.text}
                          </Typography>
                        </NavLink>
                      )}
                    </MenuItem>
                  ))}
                </SubMenu>
              ) as any)}
          </Menu>
        </nav>
        {(isHigherGradeLevel && isStudentFromGoogleClass) ||
        !isStudentFromGoogleClass ? (
          <>
            <OfflineTools />
            <div className={styles.controls}>
              <UserNav />
            </div>
          </>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
